import { FunctionalComponent, h } from '@stencil/core'
import { Fragment, JSXBase } from '@stencil/core/internal'
import { noop, sortBy } from 'lodash-es'

import { IBookmark, ISalesforceField, isImmutable } from '../../bookmarks/types'
import { SalesforceIntegrationStore } from '../../crm/salesforce/state'
import { SlackChannel } from '../../customize/integrations/state'
import { LearnMore } from '../../knowledge-base/ui'
import { colors } from '../../tailwind'
import { colors as bookmarkColors } from '../../ui/bookmark/color-picker/types'
import { classes, colorClasses } from '../../utils/css'
import { SalesforceLogo, SlackLogo } from '../integrations/logos'
import { SlackIntegrationStore } from '../integrations/state'

export const SlackMenu: FunctionalComponent<{
  selectedChannel: SlackChannel
  onRemove: (channel: SlackChannel) => void
  onSelect: (channel: SlackChannel) => void
}> = ({ selectedChannel, onSelect, onRemove }) => {
  const sorted = sortBy(SlackIntegrationStore.state.channels, ['name'])
  // sets the name of the channel for display
  if (selectedChannel) {
    selectedChannel = sorted.find((channel) => channel.external_id === selectedChannel.external_id)
  }
  const channels = !selectedChannel
    ? sorted
    : [selectedChannel, ...sorted.filter((c) => c.external_id !== selectedChannel.external_id)]
  return (
    <button>
      <SlackLogo class='h-4' inactive={!selectedChannel} />
      <ui-tooltip on='hover' anchor='top'>
        <span>
          {selectedChannel ? (
            <Fragment>
              Connected to:
              <br />
              <span class='text-sm font-bold'>{selectedChannel.name}</span>
            </Fragment>
          ) : (
            <Fragment>
              Select a channel to send these
              <br /> highlights to in real-time <LearnMore page='slack' />
            </Fragment>
          )}
        </span>
      </ui-tooltip>
      <ui-tooltip
        name='integration-menu'
        anchor='left'
        on='click'
        persist
        bgColor='surface-base'
        borderColor='surface-higher'
      >
        <ui-scrollarea style={{ maxHeight: '33vh' }} shadowStyleY={{ height: '1em' }}>
          <div class='flex flex-col p-1 spacing-y-2 '>
            {channels.map((c) => {
              const isSelected = selectedChannel && selectedChannel.external_id === c.external_id
              return (
                <section class='flex items-center' style={{ maxWidth: '25ch' }}>
                  <button
                    class={`
                      flex-grow text-left truncate hover:text-brand
                      ${classes(isSelected, 'text-brand')}
                    `}
                    style={{ maxWidth: '25ch' }}
                    onClick={() => onSelect(c)}
                  >
                    #{c.name}
                  </button>
                  {isSelected && (
                    <ui-icon
                      class='mr-4 text-glyph-lightest hover:text-info-error cursor-pointer'
                      icon='x-circle'
                      onClick={() => onRemove(c)}
                    ></ui-icon>
                  )}
                </section>
              )
            })}
          </div>
        </ui-scrollarea>
      </ui-tooltip>
    </button>
  )
}

export const SalesforceMenu: FunctionalComponent<{
  bookmark: IBookmark
  onRemove: (field: ISalesforceField) => void
  onSelect: (field: ISalesforceField) => void
  renderButton?: (inactive: boolean) => void
  tooltipAnchor?: HTMLUiTooltipElement['anchor']
}> = ({ bookmark, onSelect, onRemove, renderButton, tooltipAnchor = 'left' }) => {
  const selectedField = SalesforceIntegrationStore.state.fields.find((f) => f.bookmark_id === bookmark.id)
  const sorted = sortBy(SalesforceIntegrationStore.state.fields, ['name'])
  const fields = !selectedField ? sorted : [selectedField, ...sorted.filter((f) => f.name !== selectedField.name)]
  const disabledFields = fields.filter(
    (f) => (f.bookmark_id && f !== selectedField) || (selectedField?.bookmark_id && f !== selectedField)
  )
  return (
    <button>
      {renderButton ? renderButton(!selectedField) : <SalesforceLogo class='h-4' inactive={!selectedField} />}
      <ui-tooltip anchor='top' on='hover'>
        <span>
          {selectedField ? (
            <Fragment>
              Linked to Opportunity Field:
              <br />
              <span class='text-sm font-bold'>{selectedField.label}</span>
            </Fragment>
          ) : (
            <Fragment>
              Send all notes from these <br /> highlights to an Opportunity Field
            </Fragment>
          )}
        </span>
      </ui-tooltip>
      <ui-tooltip
        name='integration-menu'
        anchor={tooltipAnchor}
        on='click'
        persist
        bgColor='surface-base'
        borderColor='surface-higher'
      >
        <ui-scrollarea style={{ maxHeight: '33vh' }} shadowStyleY={{ height: '1em' }}>
          <div class='flex flex-col p-1 spacing-y-2'>
            {fields.map((f) => {
              const isSelected = selectedField && selectedField.name === f.name
              return (
                <section
                  class={`flex items-center ${classes(disabledFields.includes(f), 'pointer-events-none opacity-25')}`}
                >
                  <button
                    class={`
                      truncate flex-grow text-left hover:text-brand
                      ${classes(isSelected, 'text-brand')}
                    `}
                    style={{ maxWidth: '25ch' }}
                    onClick={() => onSelect(f)}
                  >
                    {f.label}
                  </button>
                  {isSelected && (
                    <ui-icon
                      class='mx-2 text-glyph-lightest hover:text-brand cursor-pointer'
                      icon='x-circle'
                      onClick={() => onRemove(f)}
                    ></ui-icon>
                  )}
                </section>
              )
            })}
          </div>
        </ui-scrollarea>
      </ui-tooltip>
    </button>
  )
}

export const BookmarkColor: FunctionalComponent<
  {
    bookmark: IBookmark
    disabled?: boolean
    editable?: boolean
    onChange: (bookmark: IBookmark) => void
  } & Omit<JSXBase.IntrinsicElements['button'], 'onChange'>
> = ({ class: className = '', bookmark, disabled, onChange, editable = true, ...attrs }) => (
  <button
    class={`
      w-4 h-4 rounded transition duration-200
      ${colorClasses({ bg: bookmark.hex_color })}
      ${classes(disabled, 'pointer-events-none')}
      ${classes(!editable, 'pointer-events-none')}
      ${className}
    `}
    {...attrs}
  >
    <ui-tooltip persist on='hover' bgColor='surface-base' borderColor='surface-higher'>
      <menu class='grid grid-cols-5 gap-1 py-1 px-0 m-0'>
        {bookmarkColors.map((hex_color) => (
          <button
            close-tooltip
            class={`w-4 h-4 rounded-sm ${colorClasses({ bg: hex_color })}`}
            onClick={() => {
              onChange({
                ...bookmark,
                hex_color,
              })
            }}
          />
        ))}
      </menu>
    </ui-tooltip>
  </button>
)

export const BookmarkRow: FunctionalComponent<
  {
    bookmark: IBookmark
    dragging: boolean
    editing: boolean
    onDragHandle?: (e: MouseEvent, bookmark: IBookmark) => void
    onDelete?: (bookmark: IBookmark) => void
    onChange?: (bookmark: IBookmark) => void
    onSlackChannelMapped?: (channel: SlackChannel, bookmark: IBookmark) => void
    onSalesforceFieldMapped?: (field: ISalesforceField, bookmark: IBookmark) => void
  } & JSXBase.IntrinsicElements['li']
> = ({
  bookmark,
  dragging,
  editing,
  onDragHandle = noop,
  onDelete = noop,
  onChange = noop,
  onSlackChannelMapped = noop,
  onSalesforceFieldMapped = noop,
  class: className,
  onFocus = noop,
  onBlur = noop,
  ...attrs
}) => {
  const { state: slack } = SlackIntegrationStore
  const { state: salesforce } = SalesforceIntegrationStore
  return (
    <li
      key={bookmark.id ? `bookmark-${bookmark.id}` : 'bookmark-new'}
      class={`
        ${className} py-3px flex items-center spacing-x-1
        ${classes(bookmark.bookmark, 'hidden')}
      `}
      {...attrs}
    >
      <ui-icon
        class={`
          text-surface-higher hover:text-glyph-lighter duration-200
          ${classes(editing, 'invisible')}
          ${classes(isImmutable(bookmark), 'invisible')}
        `}
        tabIndex={-1}
        size='2xl'
        style={{ cursor: 'grab' }}
        icon='chevron-expand'
        onMouseDown={(e) => {
          if (!isImmutable(bookmark)) {
            onDragHandle(e, bookmark)
          }
        }}
      >
        <ui-tooltip anchor='top' on='hover'>
          <span class='whitespace-no-wrap'>Re-order</span>
        </ui-tooltip>
      </ui-icon>

      <div
        class={`
      flex items-center w-full p-2 py-8px rounded-md bg-surface-high spacing-x-1
      ${classes(dragging, ' transform duration-150 scale-95 opacity-50')}
    `}
      >
        <div class='flex p-1'>
          {bookmark.action_item ? (
            <ui-icon icon='check2-square' size='2xl' />
          ) : (
            <BookmarkColor
              bookmark={bookmark}
              onChange={onChange}
              disabled={editing}
              editable={!isImmutable(bookmark)}
            />
          )}
        </div>

        <input
          class={`
            uppercase focus:bg-surface-lower rounded-sm focus:no-underline focus:shadow-inner placeholder-surface-higher hover:underline border border-transparent mr-1 truncate font-extrabold flex-grow bg-transparent -mt-2px
            ${colorClasses({ text: editing ? 'glyph-light' : bookmark.hex_color })}
            ${classes(isImmutable(bookmark), 'pointer-events-none')}
          `}
          onFocus={onFocus}
          onBlur={onBlur}
          placeholder='add label…'
          style={{
            padding: '0 0.5ch',
            textDecorationStyle: 'dashed',
            textUnderlinePosition: 'under',
            textDecorationSkipInk: 'auto',
            textDecorationColor: colors['surface-highest'],
          }}
          value={bookmark.label}
          onChange={(e) =>
            onChange({
              ...bookmark,
              label: (e.target as HTMLInputElement).value,
            })
          }
        ></input>
        {slack.connectedTo && !isImmutable(bookmark) && (
          <SlackMenu
            selectedChannel={bookmark.slack_channel as SlackChannel}
            onSelect={(channel) => {
              onSlackChannelMapped(channel, bookmark)
            }}
            onRemove={() => {
              onSlackChannelMapped(null, bookmark)
            }}
          />
        )}
        {salesforce.connectedTo && salesforce.fields.length > 0 && !isImmutable(bookmark) && (
          <SalesforceMenu
            bookmark={bookmark}
            onSelect={(field) => {
              onSalesforceFieldMapped(field, bookmark)
            }}
            onRemove={(field) => {
              onSalesforceFieldMapped(field, null)
            }}
          />
        )}
      </div>
      <ui-icon
        class={`
          opacity-25 cursor-pointer hover:opacity-50 transition-opacity duration-200
          ${classes(editing, 'invisible')}
          ${classes(isImmutable(bookmark), 'invisible')}
        `}
        tabIndex={-1}
        size='xl'
        icon='trash'
        onClick={() => {
          if (!isImmutable(bookmark)) {
            onDelete(bookmark)
          }
        }}
      />
    </li>
  )
}
